import { defineStore } from "pinia";
import { ref } from "vue";
import { fetchCurrentUserInfo } from "@app/api/user";
import { useAuthStore } from "@shared/store/modules/auth";
import type { CurrentUserInfoData, PermissionAction, UserDetail, UserMenuItem } from "@shared/types/user";

/** 客户端当前用户信息（会话内缓存，登录后拉取；menus 待动态路由接入） */
export const useCurrentUserStore = defineStore("currentUser", () => {
    const userDetail = ref<UserDetail | null>(null);
    const menus = ref<UserMenuItem[]>([]);
    /** 强制改密：可来自登录响应种子，或 user/info 水合 */
    const forceChangePassword = ref(false);
    const loaded = ref(false);
    const loading = ref(false);
    let inFlight: Promise<void> | null = null;
    /** 换票 / reset 时递增，丢弃过期的 in-flight info 响应 */
    let fetchGeneration = 0;

    function applyInfo(data: CurrentUserInfoData) {
        userDetail.value = data.userDetail ?? null;
        menus.value = data.menus ?? [];
        forceChangePassword.value = !!data.userDetail?.forceChangePassword;
        syncAuthDisplayName(userDetail.value, data.permissions, data.actions);
        loaded.value = true;
    }

    /** 路由守卫拉菜单时复用同一次 info，强制改密弹窗才能及时打开 */
    function hydrateFromInfo(data: CurrentUserInfoData) {
        applyInfo(data);
    }

    /** 登录接口已返回 forceChangePassword 时先种标记，不等 info */
    function seedForceChangePassword(need: boolean) {
        forceChangePassword.value = need;
    }

    /** 同步顶栏展示名/头像/权限码：优先 username（/customer/user/info） */
    function syncAuthDisplayName(detail: UserDetail | null, permissions?: string[], actions?: PermissionAction[]) {
        if (!detail) return;
        const name = detail.username || detail.nick || detail.tel || "";
        if (!name && !(detail.avatar || "").trim() && permissions === undefined && actions === undefined) return;
        const auth = useAuthStore();
        auth.setUserInfo({
            id: detail.id,
            name: name || auth.userInfo?.name || "",
            avatar: (detail.avatar || "").trim() || undefined,
            tel: detail.tel,
            email: detail.email,
            roles: auth.userInfo?.roles ?? [],
            permissions: permissions ?? auth.userInfo?.permissions ?? [],
            actions: actions ?? auth.userInfo?.actions ?? [],
        });
    }

    async function fetchInfo(force = false) {
        if (loading.value && inFlight && !force) {
            return inFlight;
        }
        if (loaded.value && !force) {
            return;
        }

        const generation = ++fetchGeneration;
        const auth = useAuthStore();
        const tokenAtStart = auth.token;
        loading.value = true;
        inFlight = (async () => {
            try {
                const data = await fetchCurrentUserInfo();
                // 换票/登出后丢弃旧响应，避免覆盖新会话
                if (generation !== fetchGeneration || useAuthStore().token !== tokenAtStart) {
                    return;
                }
                applyInfo(data);
            } catch (e) {
                if (generation !== fetchGeneration) return;
                // 刷新失败保留会话缓存，避免保存资料后顶栏/菜单被误清
                console.error("[currentUser]", e);
            } finally {
                if (generation === fetchGeneration) {
                    loading.value = false;
                    inFlight = null;
                }
            }
        })();

        return inFlight;
    }

    function fetchIfNeeded() {
        return fetchInfo(false);
    }

    function refresh() {
        return fetchInfo(true);
    }

    function reset() {
        fetchGeneration += 1;
        userDetail.value = null;
        menus.value = [];
        forceChangePassword.value = false;
        loaded.value = false;
        loading.value = false;
        inFlight = null;
    }

    return {
        userDetail,
        menus,
        forceChangePassword,
        loaded,
        loading,
        hydrateFromInfo,
        seedForceChangePassword,
        fetchIfNeeded,
        refresh,
        reset,
    };
});
